import Constants from '@/constants.js';
import moment from 'moment';

/**
 * Max value for numbers to be sent for the evaluation
 */
const maxValue = 9999;

/**
 * Truncates the values for the relevant fields in the haus data so that no value is greater than 9999
 * @param {*} data
 */
function truncateHausValues(data) {
  const itemsToTruncate = [
    'hausNutzflaecheM2',
    'grundFlaeche',
    'hausNumberOfStoreys',
    'hausBalkonM2',
    'hausKellerM2',
    'hausNebengebaeudeM2',
  ];
  itemsToTruncate.forEach((item) => {
    if (data[item] && parseInt(data[item]) > maxValue) {
      data[item] = maxValue.toString();
    }
  });
}

/**
 * Truncates the values for the relevant fields in the wohnung data so that no value is greater than 9999
 * @param {*} data
 */
function truncateWohnungValues(data) {
  const itemsToTruncate = [
    'whgNutzflaecheM2',
    'hausBalkonM2',
    'whgGaragenStk',
    'whgGartenM2',
    'whgKellerM2',
  ];
  itemsToTruncate.forEach((item) => {
    if (data[item] && parseInt(data[item]) > maxValue) {
      data[item] = maxValue.toString();
    }
  });
}

/**
 * Truncates the values for the relevant fields in the dg wohnung data so that no value is greater than 9999
 * @param {*} data
 */
function truncateDgWohnungValues(data) {
  const itemsToTruncate = [
    'whgNutzflaecheM2',
    'hausBalkonM2',
    'whgGaragenStk',
    'whgKellerM2',
  ];
  itemsToTruncate.forEach((item) => {
    if (data[item] && parseInt(data[item]) > maxValue) {
      data[item] = maxValue.toString();
    }
  });
}

/**
 * Sets the value for hausBalkonM2 by combining the values for balkon, wintergarten and terrasse
 * @param {*} data
 */
function setHausBalkonM2(data) {
  data['hausBalkonM2'] = data['hausBalkonM2'] || '0';
  data['hausWintergartenM2'] = data['hausWintergartenM2'] || '0';
  data['hausTerrasseM2'] = data['hausTerrasseM2'] || '0';
  data['hausBalkonM2'] =
    parseInt(data['hausBalkonM2']) +
    parseInt(data['hausWintergartenM2']) +
    parseInt(data['hausTerrasseM2']);
  delete data.hausWintergartenM2;
  delete data.hausTerasseM2;
}

/**
 * Sets the value for whgBalkonM2 by combining the values for balkon, wintergarten and terrasse
 * @param {*} data
 */
function setWhgBalkonM2(data) {
  data['whgBalkonM2'] = data['whgBalkonM2'] || '0';
  data['whgWintergartenM2'] = data['whgWintergartenM2'] || '0';
  data['whgTerrasseM2'] = data['whgTerrasseM2'] || '0';
  data['whgBalkonM2'] =
    parseInt(data['whgBalkonM2']) +
    parseInt(data['whgWintergartenM2']) +
    parseInt(data['whgTerrasseM2']);
  delete data.whgWintergartenM2;
  delete data.whgTerasseM2;
}

/**
 * Gets all fields from the sessionStorage that are needed for the haus evaluation
 * @returns array
 */
function getDataForHausEvaluation() {
  let data = {};
  Constants.FIELDS_HAUS.forEach((field) => {
    const fieldInStorage = Constants.PREFIX + '.' + field;
    data[field] = sessionStorage.getItem(fieldInStorage);
  });
  setHausBalkonM2(data);
  data['hausKellerM2'] = data['hausKellerM2'] || '0';
  data['hausNebengebaeudeM2'] = data['hausNebengebaeudeM2'] || '0';
  truncateHausValues(data);
  data['kaufpreis'] = '0';
  return data;
}

/**
 * Gets all fields from the sessionStorage that are needed for the wohnung evaluation
 * @returns array
 */
function getDataForWohnungEvaluation() {
  let data = {};
  Constants.FIELDS_WOHNUNG.forEach((field) => {
    const fieldInStorage = Constants.PREFIX + '.' + field;
    data[field] = sessionStorage.getItem(fieldInStorage);
  });
  setWhgBalkonM2(data);
  data['whgGaragenStk'] = data['whgGaragenStk'] || '0';
  data['flagDachgeschoss'] = 'false';
  truncateWohnungValues(data);
  data['kaufpreis'] = '0';
  /**
   * Indices for IndexArtFassade, indexWhgErhaltung, IndexArtGebaeudesanierung are required
   * Set to 10 if no value is set
   */
  data['indexArtFassade'] = data['indexArtFassade'] || 10;
  data['indexWhgErhaltung'] = data['indexWhgErhaltung'] || 10;
  data['indexArtGebaeudesanierung'] = data['indexArtGebaeudesanierung'] || 10;
  return data;
}

/**
 * Gets all fields from the sessionStorage that are needed for the dg-wohnung evaluation
 * @returns array
 */
function getDataForDgWohnungEvaluation() {
  let data = {};
  Constants.FIELDS_DG_WOHNUNG.forEach((field) => {
    const fieldInStorage = Constants.PREFIX + '.' + field;
    data[field] = sessionStorage.getItem(fieldInStorage);
  });
  setWhgBalkonM2(data);
  data['whgGaragenStk'] = data['whgGaragenStk'] || '0';
  data['flagDachgeschoss'] = 'true';
  truncateDgWohnungValues(data);
  data['kaufpreis'] = '0';
  return data;
}

/**
 * Get the values for each key in the passed object from sessionStorage
 * @param {Object} fields
 */
function getFromStorage(fields) {
  Object.keys(fields).forEach((field) => {
    const fieldInStorage = Constants.PREFIX + '.' + field;
    if (sessionStorage.getItem(fieldInStorage) != null) {
      fields[field].value = sessionStorage.getItem(fieldInStorage);
    }
  });
}

/**
 * Save the values for each key in the passed object to sessionStorage
 * @param {Object} fields
 */
function saveToStorage(fields) {
  Object.keys(fields).forEach((field) => {
    const fieldInStorage = Constants.PREFIX + '.' + field;
    if (fields[field].value != null) {
      sessionStorage.setItem(fieldInStorage, fields[field].value);
    } else {
      sessionStorage.removeItem(fieldInStorage);
    }
  });
}

/**
 * Get value of field from sessionStorage
 * @param {*} field
 * @returns string | null
 */
function getFieldFromStorage(field) {
  const fieldInStorage = Constants.PREFIX + '.' + field;
  return sessionStorage.getItem(fieldInStorage);
}

/**
 * Removes a field from sessionStorage
 * @param {*} field
 */
function removeFieldFromStorage(field) {
  const fieldInStorage = Constants.PREFIX + '.' + field;
  sessionStorage.removeItem(fieldInStorage);
}

/**
 * Clear all related items from sessionStorage
 */
function clearStorage() {
  let itemsToClear = [];
  for (let i = 0; i < sessionStorage.length; i++) {
    let key = sessionStorage.key(i);
    if (key.startsWith(Constants.PREFIX)) {
      itemsToClear.push(key);
    }
  }
  itemsToClear.forEach((key) => {
    sessionStorage.removeItem(key);
  });
}

/**
 * Checks whether a flag has the value 'true'. Flags are in string format
 * @param {String} flag
 * @returns Boolean
 */
function isSet(flag) {
  return flag === 'true';
}

/**
 * Sets all fields to default if the value is null and a deafult exits
 */
function setDefaults(fields) {
  Object.keys(fields).forEach((field) => {
    if (!fields[field].value && fields[field].default) {
      fields[field].value = fields[field].default;
    }
  });
}

/**
 * Checks for valid fields and sets their state to 'ACCEPTED'
 * @param {Object} fields
 */
function checkValidFields(fields) {
  Object.keys(fields).forEach((key) => {
    const field = fields[key];
    if (field.validators && field.validators.length > 0) {
      const status = validateField(field.value, field.validators);
      if (status && status.startsWith('ACCEPTED')) {
        field.state = status;
      }
    }
  });
}

/**
 * Checks for invalid fields and sets their state to 'ERROR'
 * @param {Object} fields
 */
function checkInvalidFields(fields) {
  Object.keys(fields).forEach((key) => {
    const field = fields[key];
    if (field.validators && field.validators.length > 0) {
      const status = validateField(field.value, field.validators);
      if (status && status.startsWith('ERROR')) {
        field.state = status;
      }
    }
  });
}

/**
 * Checks whether the fields contain invalid fields
 * @param {Object} fields
 * @returns Boolean
 */
function containsInvalidFields(fields) {
  let containsInvalid = false;
  Object.keys(fields).forEach((key) => {
    const field = fields[key];
    if (field.state && field.state.startsWith('ERROR')) {
      containsInvalid = true;
    }
  });
  return containsInvalid;
}

/**
 * Validates the value of a field against its validators and returns the resulting state
 * @param {*} value
 * @param {*} validators
 * @returns String
 */
function validateField(value, validators) {
  if (!validators || validators.length <= 0) {
    return null;
  }
  let finalStatus = 'ACCEPTED';
  validators.forEach((validator) => {
    let status = null;
    let valArray = validator.split(':');
    let valPrefix = valArray[0];
    let valArgument = valArray.length > 1 ? valArray[1] : null;
    switch (valPrefix) {
      case 'required':
        status = validateRequired(value);
        break;
      case 'email':
        if (value) {
          status = validateEmail(value);
        }
        break;
      case 'min':
        status = validateMin(value, valArgument);
        break;
      case 'max':
        status = validateMax(value, valArgument);
        break;
      case 'checked':
        status = validateChecked(value);
        break;
    }
    if (finalStatus === 'ACCEPTED' && status && status.startsWith('ERROR')) {
      finalStatus = status;
    }
  });
  return finalStatus;
}

/**
 * Checks wheter a field has been set
 * @param {*} value
 * @returns String
 */
function validateRequired(value) {
  if (!value || value.length <= 0) {
    return 'ERROR Das Feld ist verpflichtend';
  }
  return 'ACCEPTED';
}

/**
 * Checks whether a field contains a valid email address
 * @param {*} value
 * @returns String
 */
function validateEmail(value) {
  // eslint-disable-next-line
  if (!value || !/^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/.test(value)) {
    return 'ERROR Das Feld muss eine gültige Email-Adresse enthalten';
  }
  return 'ACCEPTED';
}

/**
 * Checks wheter a field contains a value smaller than the threshold
 * @param {*} value
 * @param {*} min
 * @returns String
 */
function validateMin(value, min) {
  if (!value || parseInt(value) < min) {
    return `ERROR Der Wert darf nicht kleiner als ${min} sein`;
  }
  return 'ACCEPTED';
}

/**
 * Checks wheter a field contains a value larger than the threshold
 * @param {*} value
 * @param {*} min
 * @returns String
 */
function validateMax(value, max) {
  if (!value || parseInt(value) > max) {
    return `ERROR Der Wert darf nicht größer als ${max} sein`;
  }
  return 'ACCEPTED';
}

/**
 * Checks whether a field is set to true
 * @param {*} value
 * @returns String
 */
function validateChecked(value) {
  if (value != 'true') {
    return 'ERROR Das Feld muss angekreuzt sein';
  }
  return 'ACCEPTED';
}

/**
 * Validates plz against a number of available options
 * @param {*} fields
 * @param {*} cityDetails
 * @returns String
 */
function validatePLZ(fields, cityDetails) {
  if (!fields['plz'].value || fields['plz'].length <= 0) {
    fields['plz'].state = null;
    return;
  }
  fields['plz'].state = 'ACCEPTED';
  const availableZipCodes = cityDetails.map((item) => {
    return item.zipCode;
  });
  if (!availableZipCodes.includes(fields['plz'].value)) {
    fields['plz'].state = 'ERROR Ungültige Postleitzahl';
  }
}

/**
 * Validates ort against a number of available options
 * @param {*} fields
 * @param {*} cityDetails
 * @returns String
 */
function validateOrt(fields, cityDetails) {
  if (!fields['ort'].value || fields['ort'].length <= 0) {
    fields['ort'].state = null;
    return;
  }
  fields['ort'].state = 'ACCEPTED';
  const availableCities = cityDetails.map((item) => {
    return item.city;
  });
  if (!availableCities.includes(fields['ort'].value)) {
    fields['ort'].state = 'ERROR Ungültiger Ort';
  }
}

/**
 * Validates strasse against a number of available options
 * @param {*} fields
 * @param {*} streetSuggestions
 * @returns String
 */
function validateStrasse(fields, streetSuggestions) {
  if (!fields['strasse'].value || fields['strasse'].length <= 0) {
    fields['strasse'].state = null;
    return;
  }
  fields['strasse'].state = 'ACCEPTED';
  if (!streetSuggestions.includes(fields['strasse'].value)) {
    fields['strasse'].state = 'ERROR Ungültige Straße';
  }
}

function formatPrice(number) {
  return new Intl.NumberFormat('de-DE', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(number);
}

function getAusstattungName(id) {
  switch (parseInt(id)) {
    case 20:
      return 'Sehr veraltet';
    case 40:
      return 'Nicht mehr zeitgemäß';
    case 60:
      return 'Zeitgemäß/modern';
    case 80:
      return 'Hochwertig';
    case 90:
      return 'Luxuriös';
    default:
      return '';
  }
}

function sendMailEB(data, evaluation) {
  console.log('data', data);

  if (!data.kundeVorname) {
    data.kundeVorname = 'Nicht angegeben';
  }
  if (!data.kundeNachname) {
    data.kundeNachname = 'Nicht angegeben';
  }
  if (!data.kundeEmail) {
    data.kundeEmail = 'Nicht angegeben';
  }

  let indication =
    'EUR ' +
    formatPrice(evaluation.vonVerkehrswert) +
    ' - ' +
    formatPrice(evaluation.bisVerkehrswert);
  let address =
    data.plz + ' ' + data.ort + ', ' + data.strasse + ' ' + data.hausnr;
  if (data.topnr) {
    address += ', Top ' + data.topnr;
  }
  if (data.whgStockIst) {
    address += ', ' + data.whgStockIst + '. Stock';
  }

  var formPayload = {
    ':formid': 'sreal-lead',
    ':formstart':
      '/content/sites/at/spk-erstebank/www_sparkasse_at/de/configuration/forms/privatkunden/IBW-sreal-lead/sreal/jcr:content/modalParsys/start',
    _charset_: 'UTF-8',
    ':redirect': '/erstebank/configuration/leads/vielen-dank.modal.html',
    fe916d1bb1b71885fff346ecc85a0e3f: '',
    vorname: data.kundeVorname,
    nachname: data.kundeNachname,
    email: data.kundeEmail,
    indication: indication,
    adresse: address,
    baujahr: data.hausBaujahr || data.gebaeudeBaujahr,
    flaeche: data.hausNutzflaecheM2 || data.whgNutzflaecheM2,
    'filename[]': [],
    formStartPaths: [
      '/content/sites/at/spk-erstebank/www_sparkasse_at/de/configuration/forms/privatkunden/IBW-sreal-lead/sreal/jcr:content/modalParsys/start',
    ],
    originPath:
      '/erstebank/privatkunden/wohnen-finanzieren/wohnfinanzierung/immobilienservice/immobilienkalkulator',
    currentPage: window.location.href,
    ausstattung: getAusstattungName(data.indexWhgAusstattung),
    'location.host': 'www.sparkasse.at',
    'location.pathname':
      '/erstebank/privatkunden/wohnen-finanzieren/wohnfinanzierung/immobilienservice/immobilienkalkulator',
    submissionTime: moment().format(),
    'submitted-button-name': '',
  };

  fetch('/bin/erstegroup/form', {
    method: 'POST',
    headers: {
      'Content-Type': 'text/html; charset=iso-8859-1',
    },
    body: JSON.stringify(formPayload),
  })
    .then((response) => {
      console.log(response.status);
    })
    .catch((e) => {
      console.log('Form POST ERROR', e);
    });
}

function getAnredeValues() {
  let options = [
    { label: 'Bitte wählen', value: '' },
    { label: 'Herr', value: 'Herr' },
    { label: 'Frau', value: 'Frau' },
  ];

  if (process.env.VUE_APP_MANDANT != 'sbau') {
    options.shift();
  }

  return options;
}

export {
  getFromStorage,
  saveToStorage,
  getFieldFromStorage,
  removeFieldFromStorage,
  clearStorage,
  isSet,
  checkValidFields,
  checkInvalidFields,
  containsInvalidFields,
  validateField,
  setDefaults,
  getDataForHausEvaluation,
  getDataForWohnungEvaluation,
  getDataForDgWohnungEvaluation,
  validatePLZ,
  validateOrt,
  validateStrasse,
  validateMax,
  validateMin,
  sendMailEB,
  getAnredeValues,
  getAusstattungName,
};
